import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X, Menu } from "lucide-react";
import Logo from "./Logo";
import { CONTACT, scrollToSection } from "../lib/site";

const LINKS = [
    { id: "services", label: "Services" },
    { id: "about", label: "About" },
    { id: "work", label: "Work" },
    { id: "contact", label: "Contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 40);
        fn();
        window.addEventListener("scroll", fn, { passive: true });
        return () => window.removeEventListener("scroll", fn);
    }, []);

    const go = (id) => {
        setOpen(false);
        document.body.style.overflow = "";
        if (location.pathname !== "/") {
            navigate("/", { state: { scrollTo: id } });
        } else {
            scrollToSection(id);
        }
    };

    const goHome = () => {
        setOpen(false);
        document.body.style.overflow = "";
        if (location.pathname !== "/") navigate("/");
        else if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1 });
        else window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toggleMenu = () => {
        setOpen((v) => {
            document.body.style.overflow = !v ? "hidden" : "";
            return !v;
        });
    };

    return (
        <>
            <header
                data-testid="site-nav"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "bg-ink/80 backdrop-blur-xl border-b border-line"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="flex items-center justify-between px-5 md:px-10 h-16 md:h-[72px]">
                    <Logo onClick={goHome} />
                    <nav className="hidden md:flex items-center gap-8">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="link-line font-mono text-xs uppercase tracking-[0.2em] text-paper/80 hover:text-paper transition-colors duration-300"
                            >
                                {l.label}
                            </button>
                        ))}
                        <a
                            href="/coming-soon.html"
                            data-testid="nav-link-coming-soon"
                            className="link-line flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-paper/80 hover:text-paper transition-colors duration-300"
                        >
                            UNCUT <span className="text-vermilion">✦</span>
                        </a>
                        <button
                            data-testid="nav-cta-start"
                            onClick={() => go("contact")}
                            className="group flex items-center gap-2 bg-vermilion px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-vermilion-hover"
                        >
                            Start a project
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                    </nav>
                    <button
                        data-testid="nav-menu-toggle"
                        className="md:hidden flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-paper"
                        onClick={toggleMenu}
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
                        animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
                        exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
                        transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
                        className="fixed inset-0 z-40 bg-ink flex flex-col justify-between px-5 pt-28 pb-10"
                    >
                        <nav className="flex flex-col gap-2">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.15 + i * 0.07, duration: 0.5 }}
                                    onClick={() => go(l.id)}
                                    className="flex items-baseline gap-4 text-left"
                                >
                                    <span className="font-mono text-xs text-vermilion">0{i + 1}</span>
                                    <span className="font-display text-5xl font-light text-paper">
                                        {l.label}
                                    </span>
                                </motion.button>
                            ))}
                            <motion.a
                                href="/coming-soon.html"
                                data-testid="mobile-nav-link-coming-soon"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.15 + LINKS.length * 0.07, duration: 0.5 }}
                                onClick={() => { setOpen(false); document.body.style.overflow = ""; }}
                                className="flex items-baseline gap-4 text-left"
                            >
                                <span className="font-mono text-xs text-vermilion">05</span>
                                <span className="font-display text-5xl font-light text-paper">
                                    UNCUT <span className="text-vermilion">✦</span>
                                </span>
                            </motion.a>
                        </nav>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="flex flex-col gap-2 border-t border-line pt-6"
                        >
                            <a href={`mailto:${CONTACT.email}`} data-testid="mobile-menu-email" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                                {CONTACT.email}
                            </a>
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke/60">
                                Media · Digital · Social · PR · Business · Data
                            </span>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
